import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, Lightbulb } from "lucide-react";
import Seo from "@/components/seo/Seo";
import JsonLd from "@/components/seo/JsonLd";
import { articleLd, breadcrumbLd } from "@/data/schema";
import { getPost, POSTS } from "@/data/blog";
import CtaBand from "@/components/layout/CtaBand";
import NotFound from "@/pages/NotFound";

function Block({ block }) {
    switch (block.t) {
        case "h2":
            return <h2 className="mt-12 text-2xl tracking-tight text-foreground sm:text-3xl">{block.c}</h2>;
        case "p":
            return <p className="mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg">{block.c}</p>;
        case "ul":
            return (
                <ul className="mt-5 space-y-2.5">
                    {block.c.map((li, i) => (
                        <li key={i} className="flex gap-3 text-base leading-relaxed text-muted-foreground sm:text-lg">
                            <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gr-accent" />
                            <span>{li}</span>
                        </li>
                    ))}
                </ul>
            );
        case "callout":
            return (
                <div className="mt-8 flex gap-3 rounded-2xl border border-gr-accent/30 bg-gr-accent/10 p-5">
                    <Lightbulb className="mt-0.5 h-5 w-5 shrink-0 text-gr-primary dark:text-gr-dprimary" />
                    <p className="text-sm font-medium text-foreground sm:text-base">{block.c}</p>
                </div>
            );
        default:
            return null;
    }
}

export default function BlogPost() {
    const { slug } = useParams();
    const post = getPost(slug);
    if (!post) return <NotFound />;

    const date = new Date(post.date).toLocaleDateString("pl-PL", { year: "numeric", month: "long", day: "numeric" });

    return (
        <>
            <Seo title={post.title} path={`/blog/${post.slug}`} description={post.description} type="article" image={post.cover} />
            <JsonLd data={articleLd(post)} />
            <JsonLd
                data={breadcrumbLd([
                    { name: "Start", path: "/" },
                    { name: "Blog", path: "/blog" },
                    { name: post.title, path: `/blog/${post.slug}` },
                ])}
            />

            <article className="py-14 lg:py-20">
                <div className="container-gr max-w-3xl">
                    <Link to="/blog" data-testid="back-to-blog" className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
                        <ArrowLeft className="h-4 w-4" /> Wróć do bloga
                    </Link>
                    <div className="mt-6 flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="rounded-full bg-gr-secondary px-3 py-1 font-mono font-semibold uppercase tracking-wider text-gr-primary dark:bg-gr-dsecondary dark:text-gr-dprimary">{post.category}</span>
                        <span>{date}</span>
                        <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {post.readingTime}</span>
                    </div>
                    <h1 className="mt-5 text-balance text-3xl leading-tight tracking-tight text-foreground sm:text-4xl lg:text-5xl">{post.title}</h1>
                    <p className="mt-5 text-lg text-muted-foreground">{post.description}</p>
                </div>
                <div className="container-gr mt-10 max-w-3xl">
                    <div className="aspect-[16/8] overflow-hidden rounded-3xl border border-border bg-muted">
                        <img src={post.cover} alt={post.title} loading="lazy" className="h-full w-full object-cover" />
                    </div>
                </div>
                <div className="container-gr mt-6 max-w-3xl">
                    {post.body.map((block, i) => <Block key={i} block={block} />)}
                </div>
            </article>

            <section className="border-t border-border py-14">
                <div className="container-gr max-w-3xl">
                    <p className="font-heading text-lg font-bold text-foreground">Czytaj dalej</p>
                    <div className="mt-4 flex flex-col gap-3">
                        {POSTS.filter((p) => p.slug !== slug).map((p) => (
                            <Link key={p.slug} to={`/blog/${p.slug}`} className="flex items-center justify-between rounded-xl border border-border bg-card p-4 transition-colors hover:bg-secondary">
                                <span className="font-medium text-foreground">{p.title}</span>
                                <span className="font-mono text-xs text-muted-foreground">{p.readingTime}</span>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
            <CtaBand />
        </>
    );
}
